// "Complete the pair" cart upsell. The pool is server-rendered (companion
// metafields, cap 4); this element only cycles which card is visible in the
// drawer, keeps the "n of N" counter honest, remembers the × dismissal for
// the session, and one-taps ADD through /cart/add.js with Horizon's
// CartLinesUpdateEvent so the native cart morph re-renders everything (the
// added product drops out of the pool server-side). A MutationObserver
// re-applies the dismissed/active state after morphs patch this subtree —
// it is paused around our own writes so syncing can't loop. Card prices are
// server-rendered at catalog price; sync() re-prices them through the A/B
// tested price (the cart morph re-renders this subtree, so it runs again
// after every cart change).
import { CartLinesUpdateEvent } from '@shopify/events';
import { preorderPropsFromDataset } from '@theme/lettoria-preorder-property-builder';
import { repriceTested, onTestedPrices } from '@theme/lettoria-tested-price';

const DISMISS_KEY = 'lettoria-cart-upsell-dismissed';

class LettoriaCartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => this.onClick(event));
    this.observer = new MutationObserver(() => this.sync());
    this.sync();
    this.unsubscribePrices = onTestedPrices(() => this.sync());
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.unsubscribePrices?.();
  }

  get cards() {
    return Array.from(this.querySelectorAll('[ref-ups-card]'));
  }

  /** Runs `write` with the observer paused, then re-arms it. */
  quietly(write) {
    this.observer?.disconnect();
    write();
    this.observer?.observe(this, { childList: true, subtree: true, attributes: true });
  }

  sync() {
    this.quietly(() => {
      repriceTested(this);
      if (sessionStorage.getItem(DISMISS_KEY)) {
        this.setAttribute('hidden', '');
        return;
      }
      this.removeAttribute('hidden');

      const cards = this.cards;
      if (!cards.length) return; // page rail shows every card — nothing to cycle

      let active = cards.findIndex((card) => card.classList.contains('is-active'));
      if (active < 0) active = 0;
      this.paint(active, cards);
    });
  }

  show(index) {
    this.quietly(() => this.paint(index, this.cards));
  }

  paint(index, cards) {
    if (!cards.length) return;
    const bounded = ((index % cards.length) + cards.length) % cards.length;
    cards.forEach((card, i) => card.classList.toggle('is-active', i === bounded));
    const counter = this.querySelector('[ref-ups-count]');
    if (counter) counter.textContent = `${bounded + 1} of ${cards.length}`;
  }

  onClick(event) {
    const target = event.target;

    if (target.closest('[ref-ups-dismiss]')) {
      sessionStorage.setItem(DISMISS_KEY, '1');
      this.quietly(() => this.setAttribute('hidden', ''));
      return;
    }

    if (target.closest('[ref-ups-prev]')) {
      this.show(this.cards.findIndex((c) => c.classList.contains('is-active')) - 1);
      return;
    }

    if (target.closest('[ref-ups-next]')) {
      this.show(this.cards.findIndex((c) => c.classList.contains('is-active')) + 1);
      return;
    }

    const addButton = target.closest('[ref-ups-add]');
    if (addButton) this.add(addButton);
  }

  async add(button) {
    const variantId = parseInt(button.getAttribute('data-variant-id'), 10);
    if (!Number.isFinite(variantId)) return;

    // Armed pre-order variant: the promise properties ride along (app
    // contract — every /cart/add for an armed variant must carry them). The
    // data-po-* pair is rendered by lettoria-preorder-line-props on the button.
    const item = { id: variantId, quantity: 1 };
    const poProps = preorderPropsFromDataset(button);
    if (poProps) item.properties = poProps;

    button.setAttribute('disabled', '');
    // spinner state until the cart morph replaces this subtree (success) or
    // the catch below re-enables the button (failure)
    button.classList.add('is-adding');
    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      if (!response.ok) throw new Error(`cart add HTTP ${response.status}`);

      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const ajaxCart = await cartResponse.json();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: { items: ajaxCart.items, source: 'lettoria-cart-upsell', sourceId: this.id || 'cart-upsell' },
          }),
        })
      );
      // No local cleanup: the cart morph replaces this subtree with the
      // freshly computed pool (added product excluded server-side).
    } catch (error) {
      console.error('[lettoria-cart-upsell] add failed', error);
      button.removeAttribute('disabled');
      button.classList.remove('is-adding');
    }
  }
}

if (!customElements.get('lettoria-cart-upsell')) {
  customElements.define('lettoria-cart-upsell', LettoriaCartUpsell);
}
